/**
 * D-Sense 设计令牌（JS 侧）
 * ═══════════════════════════════════════════════════════
 * 与 src/styles/tokens.css 一一对应。
 *
 * 【为什么需要 JS 侧副本？】
 * Ant Design 的 `theme.darkAlgorithm` 需要对 seed token 做颜色派生计算
 * （colorBgBase → 派生 colorBorder / colorFill*；colorPrimary → 派生 hover / active / bg 等）。
 * 派生过程要解析真实颜色值，若传入 `var(--pt-*)` 字符串会导致解析失败、派生色失效。
 * 因此 ConfigProvider 的 theme 必须使用真实色值，不能直接用 CSS 变量。
 *
 * 【同步要求】
 * 修改任一处时，**必须同时修改另一处**，保持值完全一致。
 * 阶段 4 会加入 CI 校验脚本比对两者，防止漂移。
 * ═══════════════════════════════════════════════════════
 */

/** 背景四级：靠层级而非阴影区分 */
export const PT_BG_BASE = '#080b0f';   // 最底 / 图表区
export const PT_BG_PANEL = '#0d1117';  // 面板 / 工具栏 / 侧栏 / 容器
export const PT_BG_CARD = '#111820';   // 卡片 / 浮层 / 输入框
export const PT_BG_HOVER = '#161e28';  // 悬停

/** 边框三级 */
export const PT_BORDER = '#1e2d3d';        // 常规
export const PT_BORDER_BRIGHT = '#2a3f55'; // hover / 强调
export const PT_BORDER_SUBTLE = '#16202c'; // 内部分割线（最弱）

/** 系统交互主色：金色（保留现有品牌色） */
export const PT_PRIMARY = '#F0B429';
export const PT_PRIMARY_DIM = 'rgba(240, 180, 41, 0.12)';
export const PT_PRIMARY_HOVER = '#F7C557';
export const PT_PRIMARY_ACTIVE = '#D4A023';

/** 语义色 + dim 填充底 */
export const PT_BULL = '#00d97e';              // 涨 / 成功
export const PT_BULL_DIM = 'rgba(0, 217, 126, 0.12)';
export const PT_BEAR = '#ff5252';              // 跌 / 失败 / 危险
export const PT_BEAR_DIM = 'rgba(255, 82, 82, 0.12)';
export const PT_AMBER = '#f5a623';             // 提醒 / AI
export const PT_AMBER_DIM = 'rgba(245, 166, 35, 0.12)';
export const PT_BLUE = '#4d9fff';              // 信息 / 次要强调
export const PT_BLUE_DIM = 'rgba(77, 159, 255, 0.1)';
export const PT_PURPLE = '#a78bfa';            // 预留 / 分类标识
export const PT_PURPLE_DIM = 'rgba(167, 139, 250, 0.12)';

/** 文字三级 */
export const PT_TEXT_PRIMARY = '#e2eaf4';
export const PT_TEXT_SECONDARY = '#7a93b0';
export const PT_TEXT_DIM = '#3d5469';

/** 尺寸令牌 */
export const PT_CONTROL_H_SM = 24;   // 工具栏控件（AntD controlHeightSM 默认即 24）
export const PT_CONTROL_H_MD = 28;   // AntD 全局控件（默认 32 → 28，提升信息密度）
export const PT_CONTROL_H_LG = 36;   // 大控件（AntD 默认 40 → 36）
export const PT_RADIUS_XS = 2;       // 微徽章
export const PT_RADIUS_SM = 3;       // 按钮 / 标签
export const PT_RADIUS_MD = 5;       // 指标盒
export const PT_RADIUS_LG = 6;       // 卡片 / 浮层
export const PT_FONT_SIZE_BASE = 13; // 正文基准（AntD 默认 14 → 13）

/**
 * 便捷聚合对象。
 * 注意：AntD 的 darkAlgorithm 会对 token 做颜色派生，此处必须是真实色值。
 */
export const PT = {
    // 背景
    bgBase: PT_BG_BASE,
    bgPanel: PT_BG_PANEL,
    bgCard: PT_BG_CARD,
    bgHover: PT_BG_HOVER,

    // 边框
    border: PT_BORDER,
    borderBright: PT_BORDER_BRIGHT,
    borderSubtle: PT_BORDER_SUBTLE,

    // 主色
    primary: PT_PRIMARY,
    primaryDim: PT_PRIMARY_DIM,
    primaryHover: PT_PRIMARY_HOVER,
    primaryActive: PT_PRIMARY_ACTIVE,

    // 语义色
    bull: PT_BULL,
    bullDim: PT_BULL_DIM,
    bear: PT_BEAR,
    bearDim: PT_BEAR_DIM,
    amber: PT_AMBER,
    amberDim: PT_AMBER_DIM,
    blue: PT_BLUE,
    blueDim: PT_BLUE_DIM,
    purple: PT_PURPLE,
    purpleDim: PT_PURPLE_DIM,

    // 文字
    textPrimary: PT_TEXT_PRIMARY,
    textSecondary: PT_TEXT_SECONDARY,
    textDim: PT_TEXT_DIM,

    // 尺寸
    controlHeightSM: PT_CONTROL_H_SM,
    controlHeight: PT_CONTROL_H_MD,
    controlHeightLG: PT_CONTROL_H_LG,
    radiusXS: PT_RADIUS_XS,
    radiusSM: PT_RADIUS_SM,
    radius: PT_RADIUS_LG,
    fontSizeBase: PT_FONT_SIZE_BASE,
} as const;
